/**
 * Professional Email Notifications Hook
 * Production-ready hook for managing email notifications
 * Supports millions of users with comprehensive error handling
 */

import { useState, useCallback } from 'react';
import emailService from '../services/emailService';
import { processEmailTemplate, getEmailTemplate } from '../templates/emailTemplates';

/**
 * Custom hook for email notifications
 * @returns {Object} - Email notification functions and state
 */
export const useEmailNotifications = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  /**
   * Send practice creation notifications
   * @param {Object} practiceData - Practice information
   * @param {Object} adminData - Admin information
   * @param {Object} notificationSettings - Email preferences
   * @returns {Promise<Object>} - Result of email sending
   */
  const sendPracticeCreationNotifications = useCallback(async (practiceData, adminData, notificationSettings) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare email data
      const emailData = {
        practiceName: practiceData.practice_name,
        accountNumber: practiceData.account_number,
        status: practiceData.status,
        createdDate: new Date().toLocaleDateString(),
        adminName: `${adminData.first_name} ${adminData.last_name}`,
        adminEmail: adminData.email,
        adminPhone: adminData.phone_1,
        monitorPatients: practiceData.monitor_to_patient ? 'Yes' : 'No',
        buyingDevice: practiceData.buying_listing_device ? 'Yes' : 'No',
        leasingDevice: practiceData.leasing_listing_device ? 'Yes' : 'No',
        monitoringTypes: practiceData.monitoring_types || 'Not specified',
        dashboardUrl: `${window.location.origin}/practice-dashboard/${practiceData.id}`
      };

      // Send notifications based on settings
      const results = await Promise.allSettled([
        // Superadmin notification
        notificationSettings.email_notifications_for_superadmin &&
        emailService.sendCustomNotification({
          to: 'info@codexterlabs.com',
          subject: `New Practice Added - ${practiceData.practice_name}`,
          template: 'practiceCreation',
          data: emailData
        }),

        // Provider notification
        notificationSettings.email_notifications_for_provider &&
        emailService.sendCustomNotification({
          to: 'info@codexterlabs.com', // This would be dynamic in production
          subject: `New Practice Added - ${practiceData.practice_name}`,
          template: 'practiceCreation',
          data: emailData
        }),

        // Practice Caregiver notification
        notificationSettings.email_notifications_for_practice_caregiver &&
        emailService.sendCustomNotification({
          to: 'info@codexterlabs.com', // This would be dynamic in production
          subject: `New Practice Added - ${practiceData.practice_name}`,
          template: 'practiceCreation',
          data: emailData
        }),

        // System Caregiver notification
        notificationSettings.email_notifications_for_system_caregiver &&
        emailService.sendCustomNotification({
          to: 'info@codexterlabs.com', // This would be dynamic in production
          subject: `New Practice Added - ${practiceData.practice_name}`,
          template: 'practiceCreation',
          data: emailData
        })
      ]);

      // Check results
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      if (successful > 0) {
        setSuccess(`Practice creation notifications sent successfully (${successful} emails sent)`);
      }

      if (failed > 0) {
        setError(`Some notifications failed to send (${failed} failed)`);
      }

      return {
        success: successful > 0,
        successful,
        failed,
        results
      };

    } catch (error) {
      console.error('❌ Practice creation notification error:', error);
      setError('Failed to send practice creation notifications');
      return {
        success: false,
        error: error.message
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Send patient addition notifications
   * @param {Object} patientData - Patient information
   * @param {Object} practiceData - Practice information
   * @param {Object} notificationSettings - Email preferences
   * @returns {Promise<Object>} - Result of email sending
   */
  const sendPatientAdditionNotifications = useCallback(async (patientData, practiceData, notificationSettings) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare email data
      const emailData = {
        patientName: `${patientData.first_name} ${patientData.last_name}`,
        patientEmail: patientData.email,
        patientPhone: patientData.phone,
        patientDOB: patientData.date_of_birth,
        addedDate: new Date().toLocaleDateString(),
        practiceName: practiceData.practice_name,
        providerName: patientData.provider_name || 'Not assigned',
        caregiverName: patientData.caregiver_name || 'Not assigned',
        patientDashboardUrl: `${window.location.origin}/patient-dashboard/${patientData.id}`
      };

      // Send notifications based on settings
      const results = await Promise.allSettled([
        // Superadmin notification
        notificationSettings.email_notifications_for_superadmin &&
        emailService.sendCustomNotification({
          to: 'info@codexterlabs.com',
          subject: `New Patient Added - ${emailData.patientName}`,
          template: 'patientAddition',
          data: emailData
        }),

        // Provider notification
        notificationSettings.email_notifications_for_provider &&
        emailService.sendCustomNotification({
          to: patientData.provider_email || 'info@codexterlabs.com',
          subject: `New Patient Added - ${emailData.patientName}`,
          template: 'patientAddition',
          data: emailData
        }),

        // Practice Caregiver notification
        notificationSettings.email_notifications_for_practice_caregiver &&
        emailService.sendCustomNotification({
          to: patientData.caregiver_email || 'info@codexterlabs.com',
          subject: `New Patient Added - ${emailData.patientName}`,
          template: 'patientAddition',
          data: emailData
        }),

        // System Caregiver notification
        notificationSettings.email_notifications_for_system_caregiver &&
        emailService.sendCustomNotification({
          to: 'info@codexterlabs.com',
          subject: `New Patient Added - ${emailData.patientName}`,
          template: 'patientAddition',
          data: emailData
        }),

        // Patient welcome email (always sent)
        emailService.sendCustomNotification({
          to: patientData.email,
          subject: `Welcome to codexterlabs RPM Program - ${emailData.patientName}`,
          template: 'patientWelcome',
          data: {
            ...emailData,
            patientPortalUrl: `${window.location.origin}/patient-portal/${patientData.id}`
          }
        })
      ]);

      // Check results
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      if (successful > 0) {
        setSuccess(`Patient addition notifications sent successfully (${successful} emails sent)`);
      }

      if (failed > 0) {
        setError(`Some notifications failed to send (${failed} failed)`);
      }

      return {
        success: successful > 0,
        successful,
        failed,
        results
      };

    } catch (error) {
      console.error('❌ Patient addition notification error:', error);
      setError('Failed to send patient addition notifications');
      return {
        success: false,
        error: error.message
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Send custom notification
   * @param {Object} notificationConfig - Notification configuration
   * @returns {Promise<Object>} - Result of email sending
   */
  const sendCustomNotification = useCallback(async (notificationConfig) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await emailService.sendCustomNotification(notificationConfig);

      if (result.success) {
        setSuccess('Custom notification sent successfully');
      } else {
        setError(result.message);
      }

      return result;

    } catch (error) {
      console.error('❌ Custom notification error:', error);
      setError('Failed to send custom notification');
      return {
        success: false,
        error: error.message
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get email logs
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} - Email logs
   */
  const getEmailLogs = useCallback(async (filters = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await emailService.getEmailLogs(filters);

      if (!result.success) {
        setError(result.message);
      }

      return result;

    } catch (error) {
      console.error('❌ Email logs error:', error);
      setError('Failed to get email logs');
      return {
        success: false,
        error: error.message
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear notifications state
   */
  const clearNotifications = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return {
    // State
    isLoading,
    error,
    success,

    // Functions
    sendPracticeCreationNotifications,
    sendPatientAdditionNotifications,
    sendCustomNotification,
    getEmailLogs,
    clearNotifications
  };
};

export default useEmailNotifications;
